import { Component, DestroyRef, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type {
  MergeAccountsConfirmDto,
  MergeAccountsPreviewDto,
  MergeAccountsSlotCandidateDto,
  MergeAccountsSlotDto,
  OtpRequestDto,
} from '@api/models';
import { SlotCandidateOwner } from '@api/models';

/** Which account a chosen identifier comes from - the request side of {@link SlotCandidateOwner} */
type SlotChoice = MergeAccountsConfirmDto['emailChoice'];
import { finalize } from 'rxjs/operators';
import { OTP_LENGTH } from '../../../../constants';
import { resolveHttpErrorMessage } from '../../../../helpers/resolve-http-error-message.helper';
import { CustomerService } from '../../../../services/customer.service';
import { ButtonComponent } from '../../../button/button.component';
import { OtpComponent } from '../../../otp/otp.component';

/**
 * The way out of the "this email/phone is already registered" dead end.
 *
 * Both the change-email and the change-phone modal reach this identically - the identifier the customer
 * typed belongs to another one of their own accounts, created the first time they logged in with the
 * other identifier. Proving access to that account (one OTP, the same bar as logging into it) lets us
 * absorb it into the current one, so the customer lands back in checkout with their orders intact
 * instead of being told "no" with nothing to do about it.
 */
@Component({
  selector: 'tc-merge-accounts',
  templateUrl: './merge-accounts.component.html',
  styleUrls: ['./merge-accounts.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslatePipe,
    OtpComponent,
    ButtonComponent,
    ReactiveFormsModule,
  ],
})
export class MergeAccountsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly customerService = inject(CustomerService);

  /** The email or phone number that turned out to belong to another account */
  identifier = input.required<string>();
  /** The raw message from the conflict error, shown as the explanation of what happened */
  conflictMessage = input<string>(null);

  /** The merge went through - the customer document has already been refreshed */
  merged = output<void>();
  /** The customer backed out; the host modal should return to its input step */
  cancelled = output<void>();

  readonly form = new FormGroup({
    otpDigits: new FormArray(
      new Array(OTP_LENGTH).fill(null).map(() => new FormControl('', Validators.required)),
    ),
    emailChoice: new FormControl<SlotChoice>(null),
    phoneNumberChoice: new FormControl<SlotChoice>(null),
  });

  state = signal<'conflict' | 'otp'>('conflict');
  /** Nothing is flagged as missing until they have actually tried to submit */
  hasAttemptedSubmit = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  error = signal<string>(null);
  otpRequest = signal<OtpRequestDto>(null);
  preview = signal<MergeAccountsPreviewDto>(null);

  /** Only slots the server refuses to decide on its own get a radio; everything else resolves silently */
  ambiguousSlots(): MergeAccountsSlotDto[] {
    const preview = this.preview();
    if (!preview) {
      return [];
    }

    return [preview.email, preview.phoneNumber].filter(slot => slot?.isDecisionNeeded);
  }

  choiceControl(slot: MergeAccountsSlotDto): FormControl<SlotChoice> {
    return slot.slot === 'email' ? this.form.controls.emailChoice : this.form.controls.phoneNumberChoice;
  }

  /**
   * A candidate from the other account is shown masked until the OTP proves the customer can reach it,
   * so it is labelled by which account it belongs to rather than left as a bare string of dots.
   */
  isOwnAccount(candidate: MergeAccountsSlotCandidateDto): boolean {
    return candidate.owner === SlotCandidateOwner.Target;
  }

  /**
   * Shown under the radios once they have tried to submit, and only until they pick - selecting a
   * radio makes this false on the very change detection pass that the click triggers.
   */
  isChoiceMissing(slot: MergeAccountsSlotDto): boolean {
    return this.hasAttemptedSubmit() && !this.choiceControl(slot).getRawValue();
  }

  /** Sends the OTP to the other account and pulls back the preview of what the merge would do */
  mergeInit(): void {
    this.error.set(null);
    this.isLoading.set(true);

    this.customerService.mergeAccountsInit({ identifier: this.identifier() })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          this.otpRequest.set(response.otpRequest);
          this.preview.set(response.preview);
          this.state.set('otp');
        },
        error: error => this.error.set(resolveHttpErrorMessage(error)),
      });
  }

  mergeConfirm(): void {
    // Silently doing nothing reads as a broken button, so an outstanding choice is called out
    // underneath the radios it belongs to - see `isChoiceMissing`
    this.hasAttemptedSubmit.set(true);

    if (this.ambiguousSlots().some(slot => !this.choiceControl(slot).getRawValue())) {
      return;
    }

    if (this.form.controls.otpDigits.invalid) {
      return;
    }

    this.error.set(null);
    this.isLoading.set(true);

    const dto: MergeAccountsConfirmDto = {
      requestId: this.otpRequest().requestId,
      otpCode: this.form.controls.otpDigits.getRawValue().join(''),
      identifier: this.identifier(),
      // The side that won the radio, not the value - the server resolves it against the proven account
      emailChoice: this.form.controls.emailChoice.getRawValue() || undefined,
      phoneNumberChoice: this.form.controls.phoneNumberChoice.getRawValue() || undefined,
    };

    this.customerService.mergeAccountsConfirm(dto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.merged.emit(),
        error: error => this.error.set(resolveHttpErrorMessage(error)),
      });
  }

  goBack(): void {
    if (this.state() === 'otp') {
      this.form.controls.otpDigits.reset();
      this.error.set(null);
      this.hasAttemptedSubmit.set(false);
      this.state.set('conflict');
      return;
    }

    this.cancelled.emit();
  }
}
